import React from "react";
import styled from "styled-components";
import { Progress, Row, Col, Statistic, Alert, Button, Tag, Space } from "antd";
import {
  UserOutlined,
  WhatsAppOutlined,
  UserAddOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import DateRangeSelector from "./DateRangeSelector.jsx";
import FunnelExportButton from "./FunnelExportButton.jsx";

const FunnelStage = styled.div`
  text-align: center;
  padding: 16px;
  border-radius: 8px;
  background: ${(props) => (props.active ? "#f0f9ff" : "#fafafa")};
  border: 2px solid ${(props) => (props.active ? "#1890ff" : "#f0f0f0")};
  position: relative;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }

  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: -20px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 2px;
    background: ${(props) => (props.active ? "#1890ff" : "#d9d9d9")};
  }
`;

const FunnelAnalyticsWidget = ({
  data,
  loading,
  dateRange = 30,
  onDateRangeChange,
  showControls = true,
}) => {
  if (loading || !data) {
    return <LoadingContainer>Loading funnel analytics...</LoadingContainer>;
  }

  const { funnel, insights } = data;

  if (!funnel || Object.keys(funnel).length === 0) {
    return (
      <FunnelContainer>
        <FunnelHeader>
          <FunnelTitle>Funnel Analytics</FunnelTitle>
          {showControls && <FunnelTag color="blue">{dateRange} days</FunnelTag>}
        </FunnelHeader>
        <NoDataMessage>
          <p>
            No funnel data available. Funnel analytics will appear here once
            data is collected.
          </p>
        </NoDataMessage>
      </FunnelContainer>
    );
  }

  const stages = [
    {
      key: "whatsapp_start",
      title: "WhatsApp Start",
      icon: <WhatsAppOutlined />,
      count: funnel.stageBreakdown?.whatsapp_start?.count || 0,
      active: funnel.stageBreakdown?.whatsapp_start?.count > 0,
      color: "#1890ff",
    },
    {
      key: "lead_captured",
      title: "Lead Captured",
      icon: <UserAddOutlined />,
      count: funnel.stageBreakdown?.lead_captured?.count || 0,
      active: funnel.stageBreakdown?.lead_captured?.count > 0,
      color: "#52c41a",
    },
    {
      key: "quote_generated",
      title: "Quote Generated",
      icon: <FileTextOutlined />,
      count: funnel.stageBreakdown?.quote_generated?.count || 0,
      active: funnel.stageBreakdown?.quote_generated?.count > 0,
      color: "#faad14",
    },
    {
      key: "order_confirmed",
      title: "Order Confirmed",
      icon: <CheckCircleOutlined />,
      count: funnel.stageBreakdown?.order_confirmed?.count || 0,
      active: funnel.stageBreakdown?.order_confirmed?.count > 0,
      color: "#52c41a",
    },
  ];

  const conversionRates = funnel.conversionRates || {};
  const totalUsers = funnel.totalUsers || 0;
  const activeUsers = funnel.activeUsers || 0;

  return (
    <FunnelContainer>
      <FunnelHeader>
        <FunnelTitle>Funnel Analytics</FunnelTitle>
        {showControls && (
          <FunnelControls>
            <FunnelTag color="blue">{dateRange} days</FunnelTag>
            <DateRangeSelector
              value={dateRange}
              onChange={onDateRangeChange}
              disabled={loading}
            />
            <FunnelExportButton data={data} loading={loading} />
          </FunnelControls>
        )}
      </FunnelHeader>

      {/* Overall Stats */}
      <StatsGrid>
        <StatCard>
          <StatIcon style={{ color: "#1890ff" }}>
            <UserOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>{totalUsers}</StatNumber>
            <StatLabel>Total Users</StatLabel>
            <StatStatus>In System</StatStatus>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon style={{ color: "#52c41a" }}>
            <UserOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>{activeUsers}</StatNumber>
            <StatLabel>Active Users</StatLabel>
            <StatStatus>Currently Online</StatStatus>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon style={{ color: "#52c41a" }}>
            <CheckCircleOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>
              {totalUsers > 0
                ? Math.round((funnel.completionRate / totalUsers) * 100)
                : 0}
              %
            </StatNumber>
            <StatLabel>Completion Rate</StatLabel>
            <StatStatus>Success Rate</StatStatus>
          </StatContent>
        </StatCard>

        <StatCard>
          <StatIcon style={{ color: "#faad14" }}>
            <ArrowRightOutlined />
          </StatIcon>
          <StatContent>
            <StatNumber>{totalUsers - funnel.completionRate}</StatNumber>
            <StatLabel>In Progress</StatLabel>
            <StatStatus>Active Journey</StatStatus>
          </StatContent>
        </StatCard>
      </StatsGrid>

      {/* Funnel Visualization */}
      <FunnelSection>
        <SectionTitle>User Journey Flow</SectionTitle>
        <FunnelGrid>
          {stages.map((stage, index) => (
            <FunnelStage key={stage.key} active={stage.active}>
              <StageIcon style={{ color: stage.color }}>{stage.icon}</StageIcon>
              <StageTitle>{stage.title}</StageTitle>
              <StageCount>{stage.count}</StageCount>
              {conversionRates[stage.key] && (
                <StageConversion>
                  {Math.round(conversionRates[stage.key] * 100)}% conversion
                </StageConversion>
              )}
            </FunnelStage>
          ))}
        </FunnelGrid>
      </FunnelSection>

      {/* Actionable Insights */}
      {insights.nextBestActions && insights.nextBestActions.length > 0 && (
        <InsightsSection>
          <SectionTitle>Recommended Actions</SectionTitle>
          {insights.nextBestActions.map((action, index) => (
            <ActionAlert
              key={index}
              type={action.priority === "high" ? "warning" : "info"}
            >
              <ActionMessage>{action.message}</ActionMessage>
              <ActionButton size="small" type="primary">
                Take Action
              </ActionButton>
            </ActionAlert>
          ))}
        </InsightsSection>
      )}

      {/* Stage-specific Insights */}
      <InsightsGrid>
        <InsightCard>
          <InsightTitle>WhatsApp Users</InsightTitle>
          <InsightMetric>
            <InsightNumber
              style={{
                color:
                  insights.usersStuckAtWhatsApp > 5 ? "#ff4d4f" : "#52c41a",
              }}
            >
              {insights.usersStuckAtWhatsApp || 0}
            </InsightNumber>
            <InsightLabel>Stuck Users</InsightLabel>
          </InsightMetric>
          {insights.usersStuckAtWhatsApp > 5 && (
            <InsightTag color="red">Needs Attention</InsightTag>
          )}
        </InsightCard>

        <InsightCard>
          <InsightTitle>Lead Nurturing</InsightTitle>
          <InsightMetric>
            <InsightNumber
              style={{
                color: insights.usersStuckAtLead > 3 ? "#faad14" : "#52c41a",
              }}
            >
              {insights.usersStuckAtLead || 0}
            </InsightNumber>
            <InsightLabel>Stuck Leads</InsightLabel>
          </InsightMetric>
          {insights.usersStuckAtLead > 3 && (
            <InsightTag color="orange">Follow Up</InsightTag>
          )}
        </InsightCard>

        <InsightCard>
          <InsightTitle>Quote Follow-up</InsightTitle>
          <InsightMetric>
            <InsightNumber
              style={{
                color: insights.usersStuckAtQuote > 2 ? "#ff4d4f" : "#52c41a",
              }}
            >
              {insights.usersStuckAtQuote || 0}
            </InsightNumber>
            <InsightLabel>Pending Quotes</InsightLabel>
          </InsightMetric>
          {insights.usersStuckAtQuote > 2 && (
            <InsightTag color="red">High Priority</InsightTag>
          )}
        </InsightCard>
      </InsightsGrid>
    </FunnelContainer>
  );
};

// Styled Components
const FunnelContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 32px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FunnelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const FunnelTitle = styled.h3`
  margin: 0;
  color: #262626;
  font-size: 18px;
  font-weight: 600;
`;

const FunnelControls = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

const FunnelTag = styled(Tag)`
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #8c8c8c;
  font-size: 16px;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #8c8c8c;

  p {
    margin: 8px 0;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  background: rgba(24, 144, 255, 0.1);

  svg {
    font-size: 24px;
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatNumber = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #262626;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #8c8c8c;
  margin-bottom: 4px;
`;

const StatStatus = styled.div`
  font-size: 12px;
  color: #52c41a;
  font-weight: 500;
`;

const FunnelSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 20px 0;
  color: #262626;
  font-size: 16px;
  font-weight: 600;
`;

const FunnelGrid = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: center;
`;

const StageIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
`;

const StageTitle = styled.div`
  font-weight: 600;
  margin-bottom: 4px;
  color: #262626;
`;

const StageCount = styled.div`
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const StageConversion = styled.div`
  font-size: 12px;
  color: #666;
`;

const InsightsSection = styled.div`
  margin-bottom: 24px;
`;

const ActionAlert = styled(Alert)`
  margin-bottom: 8px;
`;

const ActionMessage = styled.span`
  flex: 1;
`;

const ActionButton = styled(Button)`
  margin-left: 16px;
`;

const InsightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
`;

const InsightCard = styled.div`
  padding: 20px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
  text-align: center;
`;

const InsightTitle = styled.div`
  font-weight: 600;
  color: #262626;
  margin-bottom: 16px;
  font-size: 14px;
`;

const InsightMetric = styled.div`
  margin-bottom: 12px;
`;

const InsightNumber = styled.div`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 4px;
`;

const InsightLabel = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const InsightTag = styled(Tag)`
  margin-top: 8px;
`;

export default FunnelAnalyticsWidget;
